import { useRef, useState } from 'react'
import { usePrayerList, prayedToday } from '../hooks/useData'
import { ScopeToggle } from './ExploreTab'
import AddPrayerSheet from './AddPrayerSheet'

// PrayerListSheet — "Your prayer list" (docs/FIX-PRAYER-LIST.md), opened
// from the This Week tile. A plain full-screen sheet (opened from a page,
// not from inside another sheet) — AddPrayerSheet stacks over it instead.
//
// Props: open, onClose, churchName, onOpenCommunityPrayer()

const GOLD = 'var(--gold)'
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif"
const ACTIONS_WIDTH = 160

export default function PrayerListSheet({ open, onClose, churchName, onOpenCommunityPrayer }) {
  const { items, loading, addPersonalPrayer, shareWithChurch, togglePrayedToday, markAnswered, removeItem, tellChurch } = usePrayerList()
  const [tab, setTab] = useState('mine')
  const [addOpen, setAddOpen] = useState(false)

  if (!open) return null

  const mine = items.filter(i => i.kind === 'mine' && !i.answeredAt)
  const mineAnswered = items.filter(i => i.kind === 'mine' && i.answeredAt)
  const community = items.filter(i => i.kind === 'community' && !i.answeredAt)

  function confirmShare(item) {
    if (window.confirm(`Share this prayer with ${churchName || 'your church'}? It'll be posted to the community prayer board.`)) {
      shareWithChurch(item)
    }
  }

  return (
    <div style={S.backdrop} onClick={e => { if (e.target === e.currentTarget) onClose?.() }}>
      <div style={S.sheet}>
        <div style={S.header}>
          <button onClick={onClose} aria-label="Back" style={S.iconBtn}>‹</button>
          <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: 'var(--ink)', textAlign: 'center' }}>Your prayer list</div>
          <button onClick={() => setAddOpen(true)} aria-label="Add a prayer" style={S.iconBtn}>＋</button>
        </div>

        <div style={S.scroll}>
          <ScopeToggle scope={tab} onChange={setTab} options={[['mine', 'My prayers'], ['community', 'Community requests']]}/>

          {loading ? (
            <div style={S.empty}>Loading…</div>
          ) : tab === 'mine' ? (
            <>
              <button type="button" onClick={() => setAddOpen(true)} style={S.addRow}>
                <span style={S.addRowIcon}>＋</span>
                <span style={S.addRowLabel}>Add a prayer…</span>
              </button>

              {mine.length === 0 && mineAnswered.length === 0 && (
                <div style={S.empty}>Nothing on your list yet — add what's on your heart.</div>
              )}

              {mine.map(item => (
                <MineCard key={item.id} item={item} churchName={churchName}
                  onTogglePrayed={() => togglePrayedToday(item.id, prayedToday(item.lastPrayedAt))}
                  onAnswered={() => markAnswered(item.id)}
                  onRemove={() => removeItem(item.id)}
                  onShare={() => confirmShare(item)}
                />
              ))}

              {mineAnswered.length > 0 && (
                <>
                  <div style={S.sectionLabel}>Answered</div>
                  {mineAnswered.map(item => (
                    <MineCard key={item.id} item={item} churchName={churchName} faded
                      onTogglePrayed={() => togglePrayedToday(item.id, prayedToday(item.lastPrayedAt))}
                      onRemove={() => removeItem(item.id)}
                      onShare={() => confirmShare(item)}
                      onTellChurch={item.isOwn && item.prayerId && !item.prayerAnsweredAt ? () => tellChurch(item) : null}
                    />
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              {community.length === 0 && (
                <div style={S.empty}>Nothing saved from Community yet.</div>
              )}
              {community.map(item => <CommunityCard key={item.id} item={item}/>)}
              <button type="button" onClick={onOpenCommunityPrayer} style={S.seeAllBtn}>
                See all {churchName || 'your church'} prayer requests ›
              </button>
            </>
          )}
        </div>
      </div>

      <AddPrayerSheet open={addOpen} onClose={() => setAddOpen(false)} churchName={churchName}
        onAdd={(text, share) => addPersonalPrayer(text, share)}/>
    </div>
  )
}

// A horizontal swipe-left reveal (Answered / Remove) — same imperative
// pointer-delta technique as StackedSheet's drag-to-close, applied
// sideways instead of vertically.
function SwipeRow({ children, onAnswered, onRemove }) {
  const rowRef = useRef(null)
  const dragRef = useRef({ x0: null, dx: 0, revealed: false })
  const [, forceRender] = useState(0)

  function onDown(e) {
    dragRef.current.x0 = e.clientX
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
    if (rowRef.current) rowRef.current.style.transition = 'none'
  }
  function onMove(e) {
    if (dragRef.current.x0 == null) return
    const base = dragRef.current.revealed ? -ACTIONS_WIDTH : 0
    const raw = base + (e.clientX - dragRef.current.x0)
    const dx = Math.max(-ACTIONS_WIDTH, Math.min(0, raw))
    dragRef.current.dx = dx
    if (rowRef.current) rowRef.current.style.transform = `translateX(${dx}px)`
  }
  function onUp() {
    if (dragRef.current.x0 == null) return
    dragRef.current.x0 = null
    if (rowRef.current) rowRef.current.style.transition = ''
    const revealed = dragRef.current.dx < -ACTIONS_WIDTH / 2
    dragRef.current.revealed = revealed
    if (rowRef.current) rowRef.current.style.transform = revealed ? `translateX(${-ACTIONS_WIDTH}px)` : 'translateX(0)'
    forceRender(n => n + 1)
  }
  function runAction(fn) {
    dragRef.current.revealed = false
    if (rowRef.current) rowRef.current.style.transform = 'translateX(0)'
    fn?.()
  }

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, marginBottom: 10 }}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'flex-end' }}>
        {onAnswered && (
          <button type="button" onClick={() => runAction(onAnswered)} style={S.swipeBtnAnswered}>Answered</button>
        )}
        <button type="button" onClick={() => runAction(onRemove)} style={S.swipeBtnRemove}>Remove</button>
      </div>
      <div ref={rowRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        style={{ position: 'relative', touchAction: 'pan-y', transition: 'transform .25s ease', background: 'var(--white)' }}>
        {children}
      </div>
    </div>
  )
}

function PrayCircle({ filled, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label={filled ? 'Prayed today' : 'Mark as prayed today'}
      style={{ ...S.circle, ...(filled ? S.circleOn : {}) }}>
      {filled && <span style={{ color: GOLD, fontSize: 12, fontWeight: 900 }}>✓</span>}
    </button>
  )
}

function MineCard({ item, churchName, faded, onTogglePrayed, onAnswered, onRemove, onShare, onTellChurch }) {
  const filled = prayedToday(item.lastPrayedAt)
  const card = (
    <div style={{ ...S.card, ...(faded ? S.cardFaded : {}) }}>
      <PrayCircle filled={filled} onClick={onTogglePrayed}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.cardText}>{item.text}</div>
        {item.shared ? (
          <div style={S.cardMeta}>👥 Shared with {churchName || 'your church'} · 🙏 {item.prayCount} praying · 💬 {item.commentCount}</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <span style={S.cardMeta}>🔒 Only you · Added {item.date}</span>
            {onShare && (
              <button type="button" onClick={onShare} style={S.sharePill}>Share with {churchName || 'your church'}</button>
            )}
          </div>
        )}
        {onTellChurch && (
          <button type="button" onClick={onTellChurch} style={S.tellChurchBtn}>Tell {churchName || 'your church'}</button>
        )}
      </div>
    </div>
  )
  if (faded) return card
  return <SwipeRow onAnswered={onAnswered} onRemove={onRemove}>{card}</SwipeRow>
}

function CommunityCard({ item }) {
  return (
    <div style={S.card}>
      <div style={{ width: 10 }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.cardText}>{item.text}</div>
        <div style={S.cardMeta}>{item.authorName ? `From ${item.authorName}` : 'Community request'} · 🙏 {item.prayCount} praying</div>
      </div>
    </div>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: SANS },
  sheet: { background: 'var(--cream)', width: '100%', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)', minHeight: 54, background: 'var(--white)' },
  iconBtn: { background: 'rgba(31,27,22,0.06)', border: 'none', color: 'var(--stone)', width: 34, height: 34, borderRadius: 999, cursor: 'pointer', fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 16px calc(env(safe-area-inset-bottom) + 20px)' },
  empty: { fontSize: 13.5, color: 'var(--stone)', padding: '30px 10px', textAlign: 'center' },

  addRow: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--white)', border: '1.5px dashed var(--border-strong)', borderRadius: 16, padding: '12px 14px', marginBottom: 12, cursor: 'pointer', fontFamily: SANS, textAlign: 'left' },
  addRowIcon: { fontSize: 16, color: 'var(--gold-ink)', fontWeight: 800 },
  addRowLabel: { fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' },

  sectionLabel: { fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--stone)', margin: '18px 2px 10px' },

  card: { display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--white)', borderRadius: 18, padding: 14 },
  cardFaded: { opacity: 0.6, marginBottom: 10 },
  cardText: { fontSize: 14, lineHeight: 1.45, color: 'var(--ink)' },
  cardMeta: { fontSize: 11.5, color: 'var(--stone)', marginTop: 4 },
  sharePill: { background: 'var(--highlight)', border: 'none', color: 'var(--gold-ink)', borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: SANS },
  tellChurchBtn: { marginTop: 8, background: 'var(--ink)', border: 'none', color: 'var(--white)', borderRadius: 999, padding: '7px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: SANS },

  circle: { flexShrink: 0, width: 24, height: 24, borderRadius: 12, border: '1.5px solid var(--border-strong)', background: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginTop: 1 },
  circleOn: { background: 'var(--ink)', border: '1.5px solid var(--ink)' },

  swipeBtnAnswered: { width: 80, height: '100%', background: 'var(--sage)', color: 'var(--sage-ink)', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: SANS },
  swipeBtnRemove: { width: 80, height: '100%', background: '#D9685C', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: SANS },

  seeAllBtn: { width: '100%', background: 'var(--white)', border: 'none', color: 'var(--gold-ink)', borderRadius: 16, padding: '14px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: SANS, textAlign: 'center', marginTop: 4 },
}
